(() => {
  'use strict';
  const root = document.querySelector('[data-fp-assistant]');
  if (!root || root.dataset.initialized) return;
  root.dataset.initialized = 'true';
  document.body.append(root); // Keep the floating panel outside the sticky header's stacking context.
  const el = name => root.querySelector(`[data-assistant-${name}]`);
  const panel = el('dialog');
  const smallScreen = matchMedia('(max-width:600px)');
  const endpoint = root.dataset.endpoint;
  const storeKey = `fivepoint-assistant-v1:${root.dataset.country}:${root.dataset.currency}`;
  const viewKey = `${storeKey}:view`;
  const money = new Intl.NumberFormat(root.dataset.locale, { style: 'currency', currency: root.dataset.currency });
  const cards = new Map();
  let conversation = { token: null, revision: 0, messages: [], selected: [], expires: 0 };
  let file = null, previewURL = null, busy = false, adding = false, uncertainCart = false;
  let request = null, pending = null, previousFocus = null, restoring = false;
  let latestHandles = new Set();
  let opened = false;
  let restoreProducts = async () => {};
  const photoURLs = new Set();
  const handleOK = h => typeof h === 'string' && /^[a-z0-9][a-z0-9-]{0,254}$/.test(h);
  const button = (text, action) => {
    const node = document.createElement('button'); node.type = 'button'; node.textContent = text;
    node.dataset.assistantAction = action; return node;
  };
  function announce(text) { el('status').textContent = text; }
  function error(text) { el('error').textContent = text; el('error').hidden = !text; }
  function rememberView(isOpen) {
    try { sessionStorage.setItem(viewKey, JSON.stringify({ open: isOpen, path: location.pathname, savedAt: Date.now() })); }
    catch { announce('The assistant window position could not be saved for page navigation.'); }
  }
  function shouldResumeView() {
    try {
      const view = JSON.parse(sessionStorage.getItem(viewKey) || 'null');
      if (!view || view.open !== true || typeof view.path !== 'string' || !Number.isFinite(view.savedAt) || Date.now() - view.savedAt > 1800000) return false;
      // Let the destination page remain visible on phones; the same conversation
      // stays available in the launcher. A refresh of this page keeps it open.
      if (smallScreen.matches && view.path !== location.pathname) { rememberView(false); return false; }
      return true;
    } catch { announce('The assistant window state could not be restored. You can reopen it here.'); return false; }
  }
  function persist() {
    conversation.pending = pending ? { id: pending.id, text: pending.text, action: pending.action, hasPhoto: Boolean(pending.file || pending.hasPhoto) } : null;
    conversation.uncertainCart = uncertainCart;
    try { sessionStorage.setItem(storeKey, JSON.stringify(conversation)); }
    catch { announce('This conversation can continue here, but could not be saved for page navigation.'); }
    rememberView(panel.open);
  }
  function restoreState() {
    try {
      const stored = JSON.parse(sessionStorage.getItem(storeKey) || 'null');
      if (!stored) return;
      if (stored.expires <= Date.now()) { sessionStorage.removeItem(storeKey); return; }
      if (typeof stored.token !== 'string' || !/^[a-f0-9]{64}$/.test(stored.token) || !Array.isArray(stored.messages) || stored.messages.length > 50 || !Array.isArray(stored.selected) || stored.selected.length > 12 || !stored.selected.every(handleOK) || !Number.isInteger(stored.revision)) throw new Error('invalid state');
      if (stored.messages.some(m => !['user','assistant'].includes(m.role) || typeof m.text !== 'string' || (m.handles && (!Array.isArray(m.handles) || m.handles.length > 4 || !m.handles.every(handleOK))))) throw new Error('invalid messages');
      conversation = stored;
      if (stored.pending) {
        if (!/^[a-f0-9-]{36}$/.test(stored.pending.id) || typeof stored.pending.text !== 'string') throw new Error('invalid pending reply');
        pending = { ...stored.pending, recover: true }; el('retry').hidden = false;
      }
      uncertainCart = Boolean(stored.uncertainCart); el('cart-review').hidden = !uncertainCart;
    } catch { sessionStorage.removeItem(storeKey); error('The previous conversation could not be restored. Please start a new one.'); }
  }
  function open(trigger) {
    if (panel.open) return;
    previousFocus = trigger || document.activeElement;
    el('entry').hidden = true;
    smallScreen.matches ? panel.showModal() : panel.show();
    document.body.classList.toggle('fp-assistant-mobile-open', smallScreen.matches);
    document.querySelectorAll('[data-assistant-open],[data-fp-assistant-open]').forEach(n => n.setAttribute('aria-expanded', 'true'));
    el('hint').hidden = true;
    try { sessionStorage.setItem('fivepoint-assistant-hint', 'dismissed'); } catch { /* Optional hint only. */ }
    if (!restoring && !opened) el('history').scrollTop = el('history').scrollHeight;
    opened = true;
    rememberView(true);
    restoreProducts();
  }
  function close() { rememberView(false); if (panel.open) panel.close(); }
  panel.addEventListener('keydown', event => { if (event.key === 'Escape') { event.preventDefault(); close(); } });
  panel.addEventListener('close', () => {
    if (panel.open) return; // A responsive switch may have already reopened the same dialog.
    rememberView(false);
    document.body.classList.remove('fp-assistant-mobile-open');
    el('entry').hidden = Boolean(document.querySelector('#cart-drawer dialog[open]'));
    document.querySelectorAll('[data-assistant-open],[data-fp-assistant-open]').forEach(n => n.setAttribute('aria-expanded', 'false'));
    if (previousFocus?.isConnected) previousFocus.focus({ preventScroll: true });
  });
  smallScreen.addEventListener('change', () => { if (panel.open) { close(); open(previousFocus); } });
  document.addEventListener('click', event => {
    const trigger = event.target.closest('[data-fp-assistant-open]');
    if (trigger) { event.preventDefault(); open(trigger); }
  });
  document.addEventListener('fivepoint:assistant-compose', event => {
    const text = event.detail?.text;
    if (typeof text !== 'string' || !text.trim() || text.length > 3000) return;
    open(event.detail.trigger);
    if (busy || adding || pending || el('input').value.trim()) {
      error('Finish your current message first, then choose Refine with Ask FivePoint again.');
      return;
    }
    el('input').value = text;
    el('input').dispatchEvent(new Event('input'));
    el('input').focus();
  });
  const bagDialog = document.querySelector('#cart-drawer dialog');
  if (bagDialog) new MutationObserver(() => {
    if (bagDialog.open) { close(); el('entry').hidden = true; }
    else el('entry').hidden = panel.open;
  }).observe(bagDialog, { attributes: true, attributeFilter: ['open'] });

  function setBusy(value) {
    busy = value;
    el('send').hidden = value;
    el('cancel').hidden = !value;
    el('input').disabled = value;
    el('photo').disabled = value || adding;
    root.querySelectorAll('[data-assistant-action]').forEach(n => { n.disabled = value || adding; });
    el('new').disabled = value || adding;
    updateSelection();
  }
  function appendMessage(item) {
    const follow = el('history').scrollHeight - el('history').scrollTop - el('history').clientHeight < 90;
    const node = document.createElement('div'); node.className = `fp-assistant__message fp-assistant__message--${item.role}`;
    if (item.text) { const bubble = document.createElement('p'); bubble.className = 'fp-assistant__bubble'; bubble.textContent = item.text; node.append(bubble); }
    if (item.photo) {
      if (item.photo.startsWith('blob:')) { const image = document.createElement('img'); image.src = item.photo; image.alt = 'Your fabric reference'; image.className = 'fp-assistant__reference'; node.classList.add('has-photo'); node.append(image); }
      else { const caption = document.createElement('p'); caption.textContent = 'Photo reference'; node.append(caption); }
    }
    el('welcome').hidden = true;
    el('messages').append(node);
    if (follow || item.role === 'user') el('history').scrollTop = el('history').scrollHeight;
    else el('latest').hidden = false;
    return node;
  }
  async function json(url, options = {}) {
    const response = await fetch(url, { mode: 'cors', credentials: 'omit', ...options });
    let data;
    try { data = await response.json(); }
    catch { throw new Error('The assistant returned an unreadable reply. Please check the reply before sending again.'); }
    if (!response.ok) { const e = new Error(typeof data.message === 'string' ? data.message : 'The assistant could not complete this request. Please try again.'); e.status = response.status; throw e; }
    return data;
  }
  async function getCards(handles, signal) {
    if (!handles.length) return [];
    if (handles.length > 20 || !handles.every(handleOK)) throw new Error('The product response was invalid.');
    const url = new URL(root.dataset.searchUrl, location.origin);
    url.searchParams.set('q', handles.join('|')); url.searchParams.set('section_id', 'fivepoint-finder-products');
    const response = await fetch(url, { credentials: 'same-origin', signal });
    if (!response.ok) throw new Error('Product details could not be loaded. Please retry.');
    const doc = new DOMParser().parseFromString(await response.text(), 'text/html');
    if (!doc.querySelector('[data-finder-product-batch]')) throw new Error('Product details are temporarily unavailable.');
    const found = new Map([...doc.querySelectorAll('[data-finder-product]')].map(n => [n.dataset.handle, n]));
    return handles.flatMap(handle => {
      cards.delete(handle);
      const n = found.get(handle); if (!n) return []; // A product removed since recommendation is legitimately unavailable.
      const node = document.importNode(n, true);
      node.querySelectorAll('script').forEach(script => script.remove());
      node.querySelectorAll('.fp-card__ph img').forEach(img => { img.sizes = '(max-width:350px) 85vw, 200px'; });
      cards.set(handle, { node, handle, variant: node.dataset.variant, quickship: node.dataset.quickship === 'true', price: node.dataset.price === undefined ? null : Number(node.dataset.price), name: node.querySelector('.fp-card__name')?.textContent.trim() || handle });
      return [node];
    });
  }
  async function renderRecommendations(item, container, cached = false) {
    if (!item.handles?.length) return;
    const grid = document.createElement('div'); grid.className = 'fp-assistant__cards';
    const productNodes = cached ? item.handles.flatMap(h => cards.has(h) ? [cards.get(h).node.cloneNode(true)] : []) : await getCards(item.handles);
    productNodes.forEach(node => {
      const reason = item.reasons?.find(r => r.handle === node.dataset.handle)?.text;
      if (reason) { const p = document.createElement('p'); p.className = 'fp-assistant__reason'; p.textContent = reason; node.append(p); }
      grid.append(node);
    });
    container.querySelector('.fp-assistant__cards')?.remove(); container.append(grid);
    if (!productNodes.length) { const p = document.createElement('p'); p.textContent = 'These products are no longer available. Please ask for another selection.'; container.append(p); }
    updateSelection();
  }
  function updateSelection() {
    const history = el('history');
    const wasAtEnd = history.scrollHeight - history.scrollTop - history.clientHeight < 90;
    const selected = conversation.selected.map(h => cards.get(h)).filter(Boolean);
    root.querySelectorAll('[data-finder-product]').forEach(n => {
      const input = n.querySelector('[data-finder-pick]');
      const p = cards.get(n.dataset.handle);
      const valid = /^\d+$/.test(p?.variant || '') && Number.isSafeInteger(p?.price);
      input.checked = conversation.selected.includes(n.dataset.handle);
      input.disabled = !valid || busy || adding || uncertainCart;
      n.classList.toggle('is-selected', input.checked);
    });
    el('selection').hidden = !conversation.selected.length;
    el('count').textContent = `${conversation.selected.length} colour card${conversation.selected.length === 1 ? '' : 's'}`;
    const total = selected.reduce((sum, p) => sum + (Number.isSafeInteger(p.price) ? p.price : 0), 0);
    el('total').textContent = money.format(total / 100);
    const absent = conversation.selected.filter(h => !latestHandles.has(h)).length;
    el('outside').hidden = !absent; el('outside').textContent = `${absent} outside the latest results. Review selection.`;
    const unavailable = selected.length !== conversation.selected.length || selected.some(p => !/^\d+$/.test(p.variant || '') || !Number.isSafeInteger(p.price));
    const overBudget = Number.isSafeInteger(conversation.cardBudget) && total > conversation.cardBudget;
    el('add').disabled = unavailable || overBudget || busy || adding || uncertainCart;
    el('add').textContent = adding ? 'Adding…' : unavailable ? 'Review unavailable cards' : overBudget ? 'Over colour-card budget' : 'Add selected to cart';
    el('selected').replaceChildren(...conversation.selected.map(handle => {
      const row = document.createElement('div'), label = document.createElement('span');
      const product = cards.get(handle); label.textContent = product && Number.isSafeInteger(product.price) ? `${product.name} · ${money.format(product.price / 100)}` : `${product?.name || handle} · unavailable`;
      const remove = document.createElement('button'); remove.type = 'button'; remove.textContent = 'Remove'; remove.dataset.assistantRemove = handle; remove.disabled = adding;
      row.append(label, remove); return row;
    }));
    if (wasAtEnd) history.scrollTop = history.scrollHeight;
  }
  async function ensureSession() {
    if (conversation.token) return;
    const result = await json(`${endpoint}/sessions`, { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ country: root.dataset.country, currency: root.dataset.currency, language: root.dataset.locale }), signal: AbortSignal.timeout(15000) });
    if (typeof result.session_token !== 'string' || !Number.isInteger(result.revision)) throw new Error('The assistant session could not be created.');
    conversation.token = result.session_token; conversation.revision = result.revision; conversation.expires = result.expires_at * 1000; persist();
  }
  async function runTurn(turn) {
    setBusy(true); error('');
    const loading = document.createElement('p'); loading.className = 'fp-assistant__loading'; loading.textContent = 'Finding fabrics for your project…'; el('messages').append(loading);
    const follow = el('history').scrollHeight - el('history').scrollTop - el('history').clientHeight < 150;
    if (follow) el('history').scrollTop = el('history').scrollHeight;
    try {
      await ensureSession();
      let result;
      if (turn.recover) {
        const status = await json(`${endpoint}/turns/${turn.id}`, { headers: { Authorization: `Bearer ${conversation.token}` }, signal: AbortSignal.timeout(15000) });
        if (status.status === 'completed') result = status;
        else if (status.status === 'pending') throw new Error('Your reply is still being prepared. Check again shortly.');
        else if (status.status === 'failed') { pending = null; persist(); throw new Error(`${status.message} You can send a new message.`); }
        else if (status.status === 'missing' && turn.hasPhoto && !turn.file) { pending = null; persist(); throw new Error('Your photo was not received. Please attach it again and send a new message.'); }
        else if (status.status !== 'missing') throw new Error('The previous reply could not be checked.');
      }
      const data = new FormData(); data.set('text', turn.text); data.set('action', turn.action); data.set('revision', String(conversation.revision)); data.set('turn_id', turn.id);
      if (turn.file) data.set('file', turn.file, 'fabric-reference.jpg');
      request = new AbortController();
      const timer = setTimeout(() => request?.abort(), 115000);
      try { if (!result) result = await json(`${endpoint}/turns`, { method: 'POST', headers: { Authorization: `Bearer ${conversation.token}` }, body: data, signal: request.signal }); }
      finally { clearTimeout(timer); }
      if (typeof result.reply !== 'string' || !Array.isArray(result.handles) || result.handles.length > 4 || !result.handles.every(handleOK) || result.revision !== conversation.revision + 1) throw new Error('The assistant returned an incomplete reply.');
      conversation.revision = result.revision; conversation.expires = result.expires_at * 1000;
      conversation.cardBudget = Number.isSafeInteger(result.card_budget_cents) ? result.card_budget_cents : null;
      conversation.dispatchOnly = result.dispatch_only === true;
      pending = null; el('retry').hidden = true;
      const item = { role: 'assistant', text: result.reply, handles: result.handles, reasons: result.reasons || [], actions: result.actions || [] };
      conversation.messages.push(item); latestHandles = new Set(result.handles); persist();
      // Completion is observed once here, never when restoring messages/cards.
      // No conversation text, session token, photo or mutable elapsed time is sent.
      window.FivePointEvents?.emit('fp_agent_turn_complete',{turn_id:turn.id,result:result.handles.length?'success':'empty',result_count:result.handles.length},{event_id:'agent-turn:'+turn.id,occurred_at:new Date().toISOString(),source_surface:'agent',component_id:'agent-turn'});
      const message = appendMessage(item);
      await showProducts(item, message); renderActions(item, message); announce(result.reply);
      el('input').value = ''; clearAttachment();
      el('input').style.height = ''; el('input').style.overflowY = 'hidden';
      el('input').placeholder = 'Tell me what to change…';
    } catch (e) {
      if (pending) pending.recover = true;
      window.FivePointEvents?.emit('fp_interaction_error',{operation:'agent',error_code:e.name==='AbortError'?'timeout':e.status===401?'unauthorized':e instanceof TypeError?'network':'upstream'},{source_surface:'agent',component_id:'agent-turn'});
      if (e.status === 401) { pending = null; el('reset').hidden = false; }
      persist();
      error(e.name === 'AbortError' ? 'Stopped waiting. Check the reply before sending another message.' : e.message);
      el('retry').hidden = !pending;
    } finally { request = null; loading.remove(); setBusy(false); if (follow) el('history').scrollTop = el('history').scrollHeight; }
  }
  function renderActions(item, message) {
    const actions = document.createElement('div'); actions.className = 'fp-assistant__actions';
    const labels = { more: 'Show more options', fast: 'Only 24h samples', cheaper: 'Lower fabric price', relax: 'Adjust requirements', metre: 'Per metre', cards: 'Colour cards, in total' };
    for (const action of (item.actions || []).slice(0,2)) if (labels[action]) actions.append(button(labels[action], action));
    message.append(actions);
  }
  async function showProducts(item, message, cached = false) {
    try { await renderRecommendations(item, message, cached); }
    catch (e) {
      const retryCards = document.createElement('button'); retryCards.type = 'button'; retryCards.className = 'fp-assistant__recovery'; retryCards.textContent = 'Reload product details';
      retryCards.addEventListener('click', async () => { retryCards.remove(); error(''); await showProducts(item, message); });
      message.append(retryCards); error(e.message);
    }
  }
  async function submit(action = '') {
    if (busy || adding) return;
    if (pending) { error('Please check the previous reply before sending another message.'); return; }
    const text = el('input').value.trim();
    if (!action && !text && !file) return;
    pending = { id: crypto.randomUUID(), text, action, file };
    const labels = { more: 'Show me more options.', fast: 'Only show fabrics with 24-hour sample dispatch.', cheaper: 'Show lower-priced fabric options.', relax: 'Which requirement could I change?', metre: 'My budget is per metre of fabric.', cards: 'My budget is for all selected colour cards.' };
    const item = { role: 'user', text: labels[action] || text, photo: file ? 'attached' : null };
    conversation.messages.push(item); persist(); appendMessage({ ...item, photo: file ? previewURL : null });
    await runTurn(pending);
  }
  function clearAttachment() { file = null; el('attachment').hidden = true; el('file').value = ''; el('upload-note').hidden = true; }
  async function preparePhoto(candidate) {
    if (!['image/jpeg', 'image/png', 'image/webp'].includes(candidate.type) || candidate.size > 8 * 1024 * 1024 || !candidate.size) throw new Error('Choose a JPG, PNG or WebP smaller than 8 MB.');
    const url = URL.createObjectURL(candidate);
    try {
      const img = new Image(); img.src = url; await img.decode();
      if (img.naturalWidth * img.naturalHeight > 40000000) throw new Error('Choose a photo under 40 megapixels.');
      const scale = Math.min(1, 1600 / Math.max(img.naturalWidth, img.naturalHeight));
      const canvas = document.createElement('canvas'); canvas.width = Math.round(img.naturalWidth * scale); canvas.height = Math.round(img.naturalHeight * scale);
      const ctx = canvas.getContext('2d'); ctx.fillStyle = '#fff'; ctx.fillRect(0, 0, canvas.width, canvas.height); ctx.drawImage(img, 0, 0, canvas.width, canvas.height);
      const blob = await new Promise(resolve => canvas.toBlob(resolve, 'image/jpeg', .9));
      if (!blob) throw new Error('The photo could not be opened.');
      return blob;
    } finally { URL.revokeObjectURL(url); }
  }
  async function addSelected() {
    if (busy || adding || uncertainCart || el('add').disabled) return;
    const bag = document.getElementById('cart-drawer');
    if (typeof bag?.addItems !== 'function') { error('The shopping bag is unavailable. Please reload before adding.'); return; }
    adding = true; updateSelection(); error('');
    let mutationStarted = false;
    try {
      // Refresh the selected variants and prices immediately before the actual mutation.
      const selected = [...conversation.selected], before = selected.map(h => ({ handle: h, price: cards.get(h)?.price, variant: cards.get(h)?.variant }));
      await getCards(selected);
      if (selected.some(h => !/^\d+$/.test(cards.get(h)?.variant || ''))) throw new Error('A selected colour card is no longer available. Review your selection.');
      if (conversation.dispatchOnly && selected.some(h => !cards.get(h).quickship)) throw new Error('A selected product is not currently marked for 24-hour sample dispatch. Remove it or change your requirement.');
      if (before.some(p => p.price !== cards.get(p.handle).price || p.variant !== cards.get(p.handle).variant)) {
        el('selected').hidden = false; throw new Error('A selected price or option changed. Review the updated selection before adding.');
      }
      mutationStarted = true;
      await bag.addItems(selected.map(h => ({ id: cards.get(h).variant, quantity: 1 })),{source_surface:'agent',component_id:'agent-batch-add'});
      conversation.selected = []; persist(); close(); announce('Colour cards added to your shopping bag.');
    } catch (e) {
      uncertainCart = mutationStarted; error(mutationStarted ? `${e.message} Review your shopping bag before trying again.` : e.message); el('cart-review').hidden = !uncertainCart;
    } finally { adding = false; persist(); updateSelection(); }
  }
  async function reset() {
    if (busy || adding) return;
    if (conversation.token) {
      try { await json(`${endpoint}/sessions/current`, { method: 'DELETE', headers: { Authorization: `Bearer ${conversation.token}` }, signal: AbortSignal.timeout(10000) }); }
      catch (e) { if (e.status !== 401) { error(e.message); return; } }
    }
    conversation = { token: null, revision: 0, messages: [], selected: [], expires: 0 }; cards.clear(); latestHandles.clear(); pending = null;
    uncertainCart = false; el('cart-review').hidden = true; el('retry').hidden = true; el('reset').hidden = true; el('messages').replaceChildren(); el('welcome').hidden = false;
    photoURLs.forEach(url => URL.revokeObjectURL(url)); photoURLs.clear();
    clearAttachment(); el('input').value = ''; error(''); persist(); updateSelection();
    el('input').style.height = ''; el('input').placeholder = 'Describe a fabric…';
  }
  const retry = button('Check reply', ''); retry.removeAttribute('data-assistant-action'); retry.dataset.assistantRetry = ''; retry.className = 'fp-assistant__recovery'; retry.hidden = true; el('error').after(retry);
  root.addEventListener('click', async event => {
    const target = event.target.closest('button'); if (!target) return;
    if (target.hasAttribute('data-assistant-open')) open(target);
    if (target.hasAttribute('data-assistant-close')) close();
    if (target.hasAttribute('data-assistant-dismiss')) { el('hint').hidden = true; try { sessionStorage.setItem('fivepoint-assistant-hint', 'dismissed'); } catch { /* Optional presentation state; no business fact depends on storage. */ } }
    if (target.hasAttribute('data-assistant-photo')) { el('file').click(); el('upload-note').hidden = false; }
    if (target.hasAttribute('data-assistant-remove-photo')) clearAttachment();
    if (target.hasAttribute('data-assistant-starter')) {
      if (target.dataset.assistantStarter === 'photo') { el('file').click(); el('upload-note').hidden = false; }
      else { el('input').value = target.dataset.assistantStarter === 'project' ? 'I’m making ' : 'Help me choose a fabric for '; el('input').focus(); }
    }
    if (target.hasAttribute('data-assistant-action')) await submit(target.dataset.assistantAction);
    if (target.hasAttribute('data-assistant-retry') && pending && !busy) await runTurn(pending);
    if (target.hasAttribute('data-assistant-cancel')) request?.abort();
    if (target.hasAttribute('data-assistant-add')) await addSelected();
    if (target.hasAttribute('data-assistant-review')) el('selected').hidden = !el('selected').hidden;
    if (target.hasAttribute('data-assistant-remove')) { conversation.selected = conversation.selected.filter(h => h !== target.dataset.assistantRemove); persist(); updateSelection(); }
    if (target.hasAttribute('data-assistant-new')) el('reset').hidden = false;
    if (target.hasAttribute('data-assistant-reset-no')) el('reset').hidden = true;
    if (target.hasAttribute('data-assistant-reset-yes')) await reset();
    if (target.hasAttribute('data-assistant-latest')) { el('history').scrollTop = el('history').scrollHeight; el('latest').hidden = true; }
    if (target.hasAttribute('data-assistant-cart-review')) {
      const bag = document.getElementById('cart-drawer');
      if (typeof bag?.refreshAndOpen === 'function') {
        close(); await bag.refreshAndOpen();
        if (bag.querySelector('dialog[open]')) {
          conversation.selected = []; uncertainCart = false; el('cart-review').hidden = true; persist(); updateSelection();
          error('Previous selections cleared. Check quantities in your shopping bag before selecting again.');
        }
      }
    }
  });
  root.addEventListener('change', event => {
    if (!event.target.matches('[data-finder-pick]')) return;
    const handle = event.target.closest('[data-finder-product]').dataset.handle;
    conversation.selected = event.target.checked ? [...new Set([...conversation.selected, handle])].slice(0,12) : conversation.selected.filter(h => h !== handle);
    updateSelection(); persist();
  });
  el('file').addEventListener('change', async () => {
    if (busy || adding || !el('file').files[0]) return;
    try { file = await preparePhoto(el('file').files[0]); previewURL = URL.createObjectURL(file); photoURLs.add(previewURL); el('preview').src = previewURL; el('attachment').hidden = false; error(conversation.selected.length ? 'Your selected colour cards will stay selected. Review them against the new photo before adding.' : ''); }
    catch (e) { error(e.message); }
  });
  el('form').addEventListener('submit', event => { event.preventDefault(); submit(); });
  el('input').addEventListener('keydown', event => { if (event.key === 'Enter' && !event.shiftKey && !event.isComposing && !smallScreen.matches) { event.preventDefault(); submit(); } });
  el('input').addEventListener('input', () => { el('input').style.height = 'auto'; const height = el('input').scrollHeight; el('input').style.height = `${Math.min(height,106)}px`; el('input').style.overflowY = height > 106 ? 'auto' : 'hidden'; });
  restoreState();
  el('input').placeholder = conversation.messages.length ? 'Tell me what to change…' : 'Describe a fabric…';
  try { if (sessionStorage.getItem('fivepoint-assistant-hint') === 'dismissed') el('hint').hidden = true; } catch { /* The optional hint remains visible if browser storage is disabled. */ }
  if (conversation.messages.length) {
    restoring = true;
    // Restore messages now; product requests wait until the shopper opens the assistant.
    conversation.messages.forEach(item => appendMessage(item));
    restoreProducts = async () => {
      if (!restoring) return;
      restoring = false;
      try {
        const nodes = [...el('messages').children];
        const last = conversation.messages.findLastIndex(item => item.handles?.length);
        latestHandles = new Set(last >= 0 ? conversation.messages[last].handles : []);
        await getCards([...new Set([...conversation.selected, ...latestHandles])]);
        for (let i = 0; i < conversation.messages.length; i++) {
          const item = conversation.messages[i];
          if (i === last) { await showProducts(item,nodes[i],true); renderActions(item,nodes[i]); }
          else if (item.handles?.length) {
            const view = document.createElement('button'); view.type = 'button'; view.className = 'fp-assistant__recovery'; view.textContent = 'View these fabrics';
            view.addEventListener('click', async () => { view.remove(); await showProducts(item,nodes[i]); }); nodes[i].append(view);
          } else if (i === conversation.messages.length - 1) renderActions(item,nodes[i]);
        }
        updateSelection();
      } catch (e) { error(e.message); }
    };
  }
  if (shouldResumeView() && !bagDialog?.open) open(el('open'));
  // Back/forward cache can restore an older DOM snapshot after another page
  // changed the conversation or closed it. Rebuild from current tab storage.
  window.addEventListener('pageshow', event => { if (event.persisted) location.reload(); });
})();
